import { AlertCircle, CheckCircle, Loader2, Pencil, Plus, Trash2 } from 'lucide-react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';

import Header from '../components/Header';
import { Alert, AlertDescription, AlertTitle } from '../components/ui/alert';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Checkbox } from '../components/ui/checkbox';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import SupercycleInventoryTab from '../components/supercycle/SupercycleInventoryTab';

interface User {
  id: string;
  email: string;
  first_name: string;
  last_name: string;
  roles: Array<{ key: string; name: string; description: string }>;
}

interface EligibilityRow {
  id: string;
  name: string;
  shopify_product_id: string | null;
  subscription_tier_count: number;
  eligible: boolean;
  reason: string | null;
  supercycle_options_saved_count: number;
  supercycle_publish_complete: boolean;
}

interface PushResultRow {
  template_id: string;
  template_name?: string;
  shopify_product_id?: string;
  status: 'ok' | 'skipped' | 'error';
  message?: string;
}

interface TierFormRow {
  key: string;
  name: string;
  duration_count: string;
  duration_unit: string;
  initial_price: string;
  recurring_price: string;
  checkout_price: string;
  price_per_day: string;
  billing_interval_count: string;
  billing_interval_unit: string;
  supercycle_global_id: string;
  supercycle_billing_interval: string;
  supercycle_minimum_term: string;
  shopify_selling_plan_id: string;
}

/** Matches `templateIdsBodySchema` / `MAX_BULK` on the server. */
const PUSH_CHUNK = 200;

const chunkIds = <T,>(arr: T[], size: number): T[][] => {
  const out: T[][] = [];
  for (let i = 0; i < arr.length; i += size) out.push(arr.slice(i, i + size));
  return out;
};

/** Push / eligibility errors often include long JSON; Sonner default (~4s) is too short. */
const ERROR_TOAST_MS = 45_000;

const MSG_BATCH_STOPPED =
  'Not published — an earlier batch failed or the request was interrupted. Run Push again for these products.';

const apiErrText = (d: unknown, fallback: string): string => {
  if (d && typeof d === 'object' && 'message' in d) {
    const m = (d as { message: unknown }).message;
    if (typeof m === 'string') return m;
    if (m != null) {
      try {
        return JSON.stringify(m);
      } catch {
        return fallback;
      }
    }
  }
  return fallback;
};

interface ChunkedPushOutcome {
  results: PushResultRow[];
  complete: boolean;
  successfulBatches: number;
  totalBatches: number;
  firstError?: string;
}

const appendSyntheticErrors = (
  merged: PushResultRow[],
  batchIds: string[],
  message: string,
  resolveName: (id: string) => string,
) => {
  for (const id of batchIds) {
    merged.push({
      template_id: id,
      template_name: resolveName(id),
      status: 'error',
      message,
    });
  }
};

/** Default minimum-term lengths (months) when seeding a new subscription setup. */
const DEFAULT_SUBSCRIPTION_MONTHS = [1, 2, 3, 6, 12, 24] as const;

const termLabelMonths = (m: number) => `${m} month${m > 1 ? 's' : ''}`;
const termSupercycleMonths = (m: number) => `${m}.month`;

const defaultTierRowsForMonths = (): TierFormRow[] =>
  DEFAULT_SUBSCRIPTION_MONTHS.map((m) => ({
    key: crypto.randomUUID(),
    name: termLabelMonths(m),
    duration_count: String(m),
    duration_unit: 'month',
    initial_price: '0',
    recurring_price: '0',
    checkout_price: '',
    price_per_day: '',
    billing_interval_count: '1',
    billing_interval_unit: 'month',
    supercycle_global_id: '',
    supercycle_billing_interval: '1.month',
    supercycle_minimum_term: termSupercycleMonths(m),
    shopify_selling_plan_id: '',
  }));

const emptyTierRow = (): TierFormRow => ({
  key: crypto.randomUUID(),
  name: 'Subscription plan',
  duration_count: '1',
  duration_unit: 'month',
  initial_price: '0',
  recurring_price: '0',
  checkout_price: '',
  price_per_day: '',
  billing_interval_count: '1',
  billing_interval_unit: 'month',
  supercycle_global_id: '',
  supercycle_billing_interval: '1.month',
  supercycle_minimum_term: '1.month',
  shopify_selling_plan_id: '',
});

const num = (s: string) => {
  const n = parseFloat(String(s).replace(/,/g, '').trim());
  return Number.isFinite(n) ? n : NaN;
};

const intOrUndef = (s: string) => {
  const t = s.trim();
  if (!t) return undefined;
  const n = parseInt(t, 10);
  return Number.isFinite(n) && n >= 1 ? n : NaN;
};

const numFromApi = (v: unknown): number => {
  if (v == null || v === '') return NaN;
  const n = Number(v);
  return Number.isFinite(n) ? n : NaN;
};

const formatMoneyInput = (n: number) =>
  Number.isFinite(n) ? (Math.round(n * 100) / 100).toString() : '0';

/** Map API tier + optional `recurring_from_margins` into form fields (prefill recurring when DB is still £0). */
const apiRowToForm = (t: Record<string, unknown>): TierFormRow => {
  const dbR = numFromApi(t.recurring_price);
  const marginR = numFromApi(t.recurring_from_margins);
  const recurringVal =
    Number.isFinite(dbR) && dbR > 0 ? dbR : Number.isFinite(marginR) && marginR > 0 ? marginR : 0;

  const dbI = numFromApi(t.initial_price);
  const initialVal = Number.isFinite(dbI) && dbI >= 0 ? dbI : 0;

  const dbPpd = numFromApi(t.price_per_day);
  let pricePerDayStr = '';
  if (Number.isFinite(dbPpd) && dbPpd > 0) {
    pricePerDayStr = formatMoneyInput(dbPpd);
  } else if (recurringVal > 0) {
    pricePerDayStr = formatMoneyInput(Math.round((recurringVal / 30) * 10000) / 10000);
  }

  return {
    key: (t.id as string) || crypto.randomUUID(),
    name: String(t.name ?? ''),
    duration_count: String(t.duration_count ?? 1),
    duration_unit: String(t.duration_unit ?? 'month'),
    initial_price: formatMoneyInput(initialVal),
    recurring_price: formatMoneyInput(recurringVal),
    checkout_price: (() => {
      const dbC = numFromApi(t.checkout_price);
      return Number.isFinite(dbC) && dbC >= 0 ? formatMoneyInput(dbC) : '';
    })(),
    price_per_day: pricePerDayStr,
    billing_interval_count: t.billing_interval_count != null ? String(t.billing_interval_count) : '',
    billing_interval_unit: String(t.billing_interval_unit ?? ''),
    supercycle_global_id: String(t.supercycle_global_id ?? ''),
    supercycle_billing_interval: String(t.supercycle_billing_interval ?? ''),
    supercycle_minimum_term: String(t.supercycle_minimum_term ?? ''),
    shopify_selling_plan_id: String(t.shopify_selling_plan_id ?? ''),
  };
};

export default function SupercycleSubscriptionPush() {
  const navigate = useNavigate();
  const [user, setUser] = useState<User | null>(null);
  const [rows, setRows] = useState<EligibilityRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [search, setSearch] = useState('');
  const [pushing, setPushing] = useState(false);
  const [pushProgress, setPushProgress] = useState<{
    chunkIndex: number;
    chunkCount: number;
    completed: number;
    total: number;
  } | null>(null);
  const [pushBanner, setPushBanner] = useState<{ title: string; body: string } | null>(null);
  const [lastResults, setLastResults] = useState<PushResultRow[] | null>(null);

  const nameById = useCallback((id: string) => rows.find((r) => r.id === id)?.name ?? id, [rows]);

  const [editorOpen, setEditorOpen] = useState(false);
  const [editorTemplate, setEditorTemplate] = useState<EligibilityRow | null>(null);
  const [tierRows, setTierRows] = useState<TierFormRow[]>([]);
  const [editorLoading, setEditorLoading] = useState(false);
  const [editorSaving, setEditorSaving] = useState(false);

  const token = () => localStorage.getItem('token') || '';

  useEffect(() => {
    const t = localStorage.getItem('token');
    const u = localStorage.getItem('user');
    if (!t || !u) {
      navigate('/login');
      return;
    }
    const parsed = JSON.parse(u) as User;
    setUser(parsed);
    if (!parsed.roles?.some((r) => r.key === 'admin')) {
      navigate('/dashboard');
      toast.error('Admin access required');
    }
  }, [navigate]);

  const loadEligibility = useCallback(async (opts?: { silent?: boolean }) => {
    const silent = Boolean(opts?.silent);
    if (!silent) setLoading(true);
    try {
      const r = await fetch('/api/supercycle/subscription-push/eligibility', {
        headers: { Authorization: `Bearer ${token()}` },
      });
      const d = await r.json();
      if (!r.ok) throw new Error(d.message || 'Failed to load');
      const raw = Array.isArray(d.data) ? d.data : [];
      setRows(
        raw.map((item: unknown) => {
          const x = item as Record<string, unknown>;
          return {
            id: String(x.id ?? ''),
            name: String(x.name ?? ''),
            shopify_product_id: x.shopify_product_id != null ? String(x.shopify_product_id) : null,
            subscription_tier_count: Number(x.subscription_tier_count) || 0,
            eligible: Boolean(x.eligible),
            reason: x.reason != null ? String(x.reason) : null,
            supercycle_options_saved_count: Number(x.supercycle_options_saved_count) || 0,
            supercycle_publish_complete: Boolean(x.supercycle_publish_complete),
          };
        }),
      );
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Failed to load eligibility', { duration: ERROR_TOAST_MS });
    } finally {
      if (!silent) setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (user?.roles?.some((r) => r.key === 'admin')) loadEligibility();
  }, [user, loadEligibility]);

  const reloadEditorTiers = useCallback(async (templateId: string) => {
    const res = await fetch(`/api/asset-templates/${templateId}/subscription-pricing-tiers`, {
      headers: { Authorization: `Bearer ${token()}` },
    });
    const d = await res.json();
    if (!res.ok) throw new Error(d.message || 'Failed to load tiers');
    const list = (d.data?.tiers as Record<string, unknown>[]) || [];
    setTierRows(list.length ? list.map(apiRowToForm) : defaultTierRowsForMonths());
  }, []);

  const openTierEditor = async (r: EligibilityRow) => {
    setEditorTemplate(r);
    setEditorOpen(true);
    setEditorLoading(true);
    setTierRows([]);
    try {
      await reloadEditorTiers(r.id);
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Failed to load tiers');
      setTierRows(defaultTierRowsForMonths());
    } finally {
      setEditorLoading(false);
    }
  };

  const saveTierEditor = async () => {
    if (!editorTemplate) return;
    const payload: Record<string, unknown>[] = [];
    for (const row of tierRows) {
      const duration_count = intOrUndef(row.duration_count);
      const initial_price = num(row.initial_price);
      const recurring_price = num(row.recurring_price);
      if (!row.name.trim()) {
        toast.error('Each tier needs a name');
        return;
      }
      if (duration_count === undefined || Number.isNaN(duration_count)) {
        toast.error(`Invalid duration for “${row.name}”`);
        return;
      }
      if (Number.isNaN(initial_price) || initial_price < 0 || Number.isNaN(recurring_price) || recurring_price < 0) {
        toast.error(`Invalid prices for “${row.name}”`);
        return;
      }
      const checkout_price = row.checkout_price.trim() === '' ? null : num(row.checkout_price);
      if (checkout_price !== null && (Number.isNaN(checkout_price) || checkout_price < 0)) {
        toast.error(`Invalid checkout price for “${row.name}”`);
        return;
      }
      const price_per_day = row.price_per_day.trim() === '' ? null : num(row.price_per_day);
      if (row.price_per_day.trim() !== '' && (Number.isNaN(price_per_day as number) || (price_per_day as number) < 0)) {
        toast.error(`Invalid price per day for “${row.name}”`);
        return;
      }
      const billing_interval_count = intOrUndef(row.billing_interval_count);
      payload.push({
        name: row.name.trim(),
        duration_count,
        duration_unit: row.duration_unit.trim() || 'month',
        initial_price,
        recurring_price,
        checkout_price,
        price_per_day: price_per_day === null || Number.isNaN(price_per_day) ? null : price_per_day,
        billing_interval_count: billing_interval_count === undefined || Number.isNaN(billing_interval_count) ? null : billing_interval_count,
        billing_interval_unit: row.billing_interval_unit.trim() || null,
        supercycle_global_id: row.supercycle_global_id.trim() || null,
        supercycle_billing_interval: row.supercycle_billing_interval.trim() || null,
        supercycle_minimum_term: row.supercycle_minimum_term.trim() || null,
        shopify_selling_plan_id: row.shopify_selling_plan_id.trim() || null,
      });
    }

    setEditorSaving(true);
    try {
      const res = await fetch(`/api/asset-templates/${editorTemplate.id}/subscription-pricing-tiers`, {
        method: 'PUT',
        headers: {
          Authorization: `Bearer ${token()}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ tiers: payload }),
      });
      const d = await res.json();
      if (!res.ok) throw new Error(d.message || 'Save failed');
      toast.success(`Saved ${d.data?.length ?? 0} subscription tier(s)`);
      setEditorOpen(false);
      await loadEligibility({ silent: true });
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Save failed');
    } finally {
      setEditorSaving(false);
    }
  };

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return rows;
    return rows.filter((r) => r.name.toLowerCase().includes(q) || r.id.toLowerCase().includes(q));
  }, [rows, search]);

  const eligibleIds = useMemo(
    () => new Set(filtered.filter((r) => r.eligible).map((r) => r.id)),
    [filtered],
  );

  const toggle = (id: string) => {
    setSelected((prev) => {
      const n = new Set(prev);
      if (n.has(id)) n.delete(id);
      else n.add(id);
      return n;
    });
  };

  const selectAllEligibleFiltered = () => setSelected(new Set(eligibleIds));

  const clearSelection = () => setSelected(new Set());

  const runChunkedPush = async (ids: string[], resolveName: (id: string) => string): Promise<ChunkedPushOutcome> => {
    const batches = chunkIds(ids, PUSH_CHUNK);
    const merged: PushResultRow[] = [];
    let completed = 0;
    const total = ids.length;
    const totalBatches = batches.length;

    const abortRest = (failedIndex: number, failedBatch: string[], errSummary: string): ChunkedPushOutcome => {
      appendSyntheticErrors(merged, failedBatch, `Publish failed: ${errSummary}`, resolveName);
      for (let j = failedIndex + 1; j < batches.length; j++) {
        appendSyntheticErrors(merged, batches[j], MSG_BATCH_STOPPED, resolveName);
      }
      return {
        results: merged,
        complete: false,
        successfulBatches: failedIndex,
        totalBatches,
        firstError: errSummary,
      };
    };

    for (let i = 0; i < batches.length; i++) {
      setPushProgress({
        chunkIndex: i + 1,
        chunkCount: batches.length,
        completed,
        total,
      });
      let r: Response;
      let d: unknown;
      try {
        r = await fetch('/api/supercycle/subscription-push', {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${token()}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ template_ids: batches[i] }),
        });
        try {
          d = await r.json();
        } catch {
          d = { message: `Server returned non-JSON (HTTP ${r.status})` };
        }
      } catch (e) {
        const msg = e instanceof Error ? e.message : 'Network error';
        return abortRest(i, batches[i], msg);
      }
      if (!r.ok) return abortRest(i, batches[i], apiErrText(d, `Request failed (HTTP ${r.status})`));
      merged.push(...(Array.isArray((d as { data?: unknown }).data) ? (d as { data: PushResultRow[] }).data : []));
      completed += batches[i].length;
    }
    return {
      results: merged,
      complete: true,
      successfulBatches: totalBatches,
      totalBatches,
    };
  };

  const applyPushOutcome = (ids: string[], outcome: ChunkedPushOutcome) => {
    setLastResults(outcome.results);
    const ok = outcome.results.filter((x) => x.status === 'ok').length;
    if (outcome.complete) {
      setPushBanner(null);
      toast.success(`Pushed ${ok} / ${ids.length}`);
      return;
    }
    const errored = outcome.results.filter((x) => x.status === 'error');
    setSelected(new Set(errored.map((x) => x.template_id)));
    const errShort =
      outcome.firstError && outcome.firstError.length > 280 ? `${outcome.firstError.slice(0, 280)}…` : outcome.firstError;
    setPushBanner({
      title: 'Publish did not finish',
      body: `Only ${outcome.successfulBatches} of ${outcome.totalBatches} batches completed, so some products were never sent. ${errored.length} product(s) must be published again — they stay selected in the table. Check “Last push results” for each line (failed requests are red; “Not published” means that batch was never reached). Fix any setup issues, then click Push selected or Push all eligible to retry.`,
    });
    toast.error('Publish stopped before all products were sent', {
      description: `${errored.length} product(s) still need a push.${errShort ? ` ${errShort}` : ''}`,
      duration: ERROR_TOAST_MS,
    });
    if (ok > 0) {
      toast.message(`Completed ${ok} of ${ids.length} in earlier batches`, { duration: 6000 });
    }
  };

  const pushSelected = async () => {
    const ids = Array.from(selected).filter((id) => eligibleIds.has(id));
    if (!ids.length) {
      toast.message('Select at least one eligible template');
      return;
    }
    setPushing(true);
    setPushProgress(null);
    setLastResults(null);
    setPushBanner(null);
    try {
      const outcome = await runChunkedPush(ids, nameById);
      applyPushOutcome(ids, outcome);
      await loadEligibility({ silent: true });
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Push failed', { duration: ERROR_TOAST_MS });
    } finally {
      setPushing(false);
      setPushProgress(null);
    }
  };

  const pushAllEligible = async () => {
    const ids = rows.filter((r) => r.eligible).map((r) => r.id);
    if (!ids.length) {
      toast.message('No eligible templates');
      return;
    }
    setSelected(new Set(ids));
    setPushing(true);
    setPushProgress(null);
    setLastResults(null);
    setPushBanner(null);
    try {
      const outcome = await runChunkedPush(ids, nameById);
      applyPushOutcome(ids, outcome);
      await loadEligibility({ silent: true });
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Push failed', { duration: ERROR_TOAST_MS });
    } finally {
      setPushing(false);
      setPushProgress(null);
    }
  };

  if (!user) return null;

  return (
    <div className="min-h-screen bg-background">
      <Header user={user} currentPage="supercycle-subscription-push" />
      <main className="container mx-auto p-6 space-y-4">
        <Tabs defaultValue="subscription">
          <TabsList>
            <TabsTrigger value="subscription">Subscription prices</TabsTrigger>
            <TabsTrigger value="inventory">Inventory</TabsTrigger>
          </TabsList>
          <TabsContent value="subscription" className="mt-4">
        <Card>
          <CardHeader>
            <CardTitle>Supercycle subscription prices</CardTitle>
            {!pushProgress ? (
              <p className="text-sm text-muted-foreground pt-1 max-w-3xl">
                <strong className="font-medium text-foreground">Synced</strong> means every subscription tier has a Supercycle
                option id stored (after a successful push). Re-push if you change prices or tiers.
              </p>
            ) : null}
            {pushProgress ? (
              <div className="flex items-center gap-2 text-sm text-muted-foreground pt-1">
                <Loader2 className="h-4 w-4 animate-spin shrink-0" aria-hidden />
                <span>
                  Publishing batch {pushProgress.chunkIndex} of {pushProgress.chunkCount} ·{' '}
                  {pushProgress.completed} of {pushProgress.total} completed
                </span>
              </div>
            ) : null}
          </CardHeader>
          <CardContent className="space-y-4">
            {pushBanner ? (
              <Alert variant="destructive">
                <AlertCircle className="h-4 w-4" />
                <AlertTitle>{pushBanner.title}</AlertTitle>
                <AlertDescription>{pushBanner.body}</AlertDescription>
              </Alert>
            ) : null}
            <div className="flex flex-wrap gap-2 items-center">
              <Input
                placeholder="Search by name or id…"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="max-w-xs"
              />
              <Button type="button" variant="outline" size="sm" onClick={() => loadEligibility()} disabled={loading}>
                Refresh
              </Button>
              <Button type="button" variant="outline" size="sm" onClick={selectAllEligibleFiltered}>
                Select eligible (filtered)
              </Button>
              <Button type="button" variant="ghost" size="sm" onClick={clearSelection}>
                Clear
              </Button>
              <Button type="button" onClick={pushSelected} disabled={pushing || !selected.size}>
                {pushing ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
                Push selected
              </Button>
              <Button type="button" variant="secondary" onClick={pushAllEligible} disabled={pushing}>
                Push all eligible
              </Button>
            </div>

            {loading ? (
              <p className="text-sm text-muted-foreground">Loading…</p>
            ) : (
              <div className="border rounded-md overflow-x-auto max-h-[min(720px,75vh)] overflow-y-auto">
                <table className="w-full text-sm">
                  <thead className="bg-muted sticky top-0">
                    <tr>
                      <th className="w-10 p-2" />
                      <th className="text-left p-2">Product</th>
                      <th className="text-left p-2">Shopify product</th>
                      <th className="text-right p-2">Tiers</th>
                      <th className="text-left p-2 min-w-[9rem]">Supercycle</th>
                      <th className="text-left p-2">Eligible</th>
                      <th className="text-left p-2 w-[120px]">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filtered.map((r) => (
                      <tr key={r.id} className="border-t">
                        <td className="p-2">
                          <Checkbox
                            checked={selected.has(r.id)}
                            onCheckedChange={() => toggle(r.id)}
                            aria-label={`Select ${r.name}`}
                          />
                        </td>
                        <td className="p-2">
                          <button
                            type="button"
                            className="text-primary hover:underline text-left"
                            onClick={() => navigate(`/assets/${r.id}`)}
                          >
                            {r.name}
                          </button>
                        </td>
                        <td className="p-2 font-mono text-xs">{r.shopify_product_id || '—'}</td>
                        <td className="p-2 text-right">{r.subscription_tier_count}</td>
                        <td className="p-2">
                          {!r.eligible ? (
                            <span className="text-muted-foreground">—</span>
                          ) : r.supercycle_publish_complete ? (
                            <Badge className="gap-1 bg-teal-700 hover:bg-teal-700">
                              <CheckCircle className="h-3 w-3" /> Synced
                            </Badge>
                          ) : r.supercycle_options_saved_count > 0 ? (
                            <Badge variant="secondary" className="gap-1 font-normal">
                              Partial {r.supercycle_options_saved_count}/{r.subscription_tier_count}
                            </Badge>
                          ) : (
                            <Badge variant="outline" className="gap-1 text-amber-900 border-amber-800/40 bg-amber-50">
                              <AlertCircle className="h-3 w-3" /> Not published
                            </Badge>
                          )}
                        </td>
                        <td className="p-2 max-w-[220px]">
                          {r.eligible ? (
                            <Badge variant="default" className="gap-1">
                              <CheckCircle className="h-3 w-3" /> Yes
                            </Badge>
                          ) : (
                            <span className="text-muted-foreground inline-flex items-start gap-1 text-xs">
                              <AlertCircle className="h-3 w-3 shrink-0 mt-0.5" />
                              <span>{r.reason || 'No'}</span>
                            </span>
                          )}
                        </td>
                        <td className="p-2">
                          <Button type="button" variant="outline" size="sm" className="h-8 gap-1" onClick={() => openTierEditor(r)}>
                            <Pencil className="h-3 w-3" />
                            Edit tiers
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            {lastResults && lastResults.length > 0 && (
              <div className="border rounded-md p-3 bg-muted/30">
                <h3 className="text-sm font-semibold mb-2">
                  Last push results
                  {pushBanner ? (
                    <span className="font-normal text-destructive"> — incomplete run (retry required for errors below)</span>
                  ) : null}
                </h3>
                {lastResults.some((x) => x.status !== 'ok') ? (
                  <p className="text-xs text-muted-foreground mb-2">
                    Rows marked error or &quot;Not published&quot; were not fully synced; run Push again after addressing any
                    issues.
                  </p>
                ) : null}
                <ul className="text-xs space-y-1 max-h-40 overflow-y-auto font-mono">
                  {lastResults.map((x) => (
                    <li key={x.template_id}>
                      <span
                        className={
                          x.status === 'ok' ? 'text-green-700' : x.status === 'skipped' ? 'text-yellow-700' : 'text-red-600'
                        }
                      >
                        {x.status}
                      </span>{' '}
                      {x.template_name || x.template_id}
                      {x.message ? ` — ${x.message}` : ''}
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </CardContent>
        </Card>
          </TabsContent>
          <TabsContent value="inventory" className="mt-4">
            <SupercycleInventoryTab />
          </TabsContent>
        </Tabs>

      <Dialog open={editorOpen} onOpenChange={(o) => !editorSaving && setEditorOpen(o)}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Subscription tiers</DialogTitle>
            <DialogDescription className="space-y-1">
              <span className="block">{editorTemplate?.name}</span>
              {editorTemplate?.shopify_product_id ? (
                <span className="block font-mono text-xs">Shopify product {editorTemplate.shopify_product_id}</span>
              ) : (
                <span className="block text-amber-700 text-xs">No Shopify product id — link the template before push.</span>
              )}
              <span className="block text-xs text-muted-foreground">
                Supplier <strong>Publish pricing</strong> creates or updates subscription rows from margin tiers when cost and
                RRP exist. Use this editor to override prices, set Supercycle option GIDs, or add tiers not from publish. A
                bulk push automatically re-imports tiers from Shopify when Supercycle reports duplicate option names (same as
                the Import from Shopify action on the asset template).
              </span>
            </DialogDescription>
          </DialogHeader>

          {editorLoading ? (
            <p className="text-sm text-muted-foreground py-6">Loading tiers…</p>
          ) : (
            <div className="space-y-6">
              {tierRows.map((row, idx) => (
                <div key={row.key} className="border rounded-lg p-3 space-y-3 bg-muted/20">
                  <div className="flex justify-between items-center">
                    <span className="text-sm font-medium">Plan {idx + 1}</span>
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      className="h-8 text-destructive"
                      onClick={() => setTierRows((prev) => prev.filter((x) => x.key !== row.key))}
                      disabled={tierRows.length <= 1}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <div className="space-y-1">
                      <Label className="text-xs">Name</Label>
                      <Input value={row.name} onChange={(e) => setTierRows((p) => p.map((x) => (x.key === row.key ? { ...x, name: e.target.value } : x)))} />
                    </div>
                    <div className="grid grid-cols-2 gap-2">
                      <div className="space-y-1">
                        <Label className="text-xs">Min. term (count)</Label>
                        <Input
                          value={row.duration_count}
                          onChange={(e) => setTierRows((p) => p.map((x) => (x.key === row.key ? { ...x, duration_count: e.target.value } : x)))}
                        />
                      </div>
                      <div className="space-y-1">
                        <Label className="text-xs">Unit</Label>
                        <Input
                          value={row.duration_unit}
                          onChange={(e) => setTierRows((p) => p.map((x) => (x.key === row.key ? { ...x, duration_unit: e.target.value } : x)))}
                        />
                      </div>
                    </div>
                    <div className="space-y-1">
                      <Label className="text-xs">Initial (£, optional — local-only; not sent to Supercycle)</Label>
                      <Input
                        value={row.initial_price}
                        onChange={(e) => setTierRows((p) => p.map((x) => (x.key === row.key ? { ...x, initial_price: e.target.value } : x)))}
                      />
                    </div>
                    <div className="space-y-1">
                      <Label className="text-xs">Recurring (£)</Label>
                      <Input
                        value={row.recurring_price}
                        onChange={(e) => setTierRows((p) => p.map((x) => (x.key === row.key ? { ...x, recurring_price: e.target.value } : x)))}
                      />
                    </div>
                    <div className="space-y-1">
                      <Label className="text-xs">Checkout (£, optional — defaults to recurring; set a value to override)</Label>
                      <Input
                        placeholder="leave blank for recurring"
                        value={row.checkout_price}
                        onChange={(e) => setTierRows((p) => p.map((x) => (x.key === row.key ? { ...x, checkout_price: e.target.value } : x)))}
                      />
                    </div>
                    <div className="space-y-1">
                      <Label className="text-xs">Price per day (£, optional)</Label>
                      <Input
                        value={row.price_per_day}
                        onChange={(e) => setTierRows((p) => p.map((x) => (x.key === row.key ? { ...x, price_per_day: e.target.value } : x)))}
                      />
                    </div>
                    <div className="space-y-1">
                      <Label className="text-xs">Supercycle option GID</Label>
                      <Input
                        placeholder="gid://supercycle/SubscriptionOption/…"
                        className="font-mono text-xs"
                        value={row.supercycle_global_id}
                        onChange={(e) => setTierRows((p) => p.map((x) => (x.key === row.key ? { ...x, supercycle_global_id: e.target.value } : x)))}
                      />
                    </div>
                    <div className="space-y-1">
                      <Label className="text-xs">Billing interval (Supercycle: N.unit)</Label>
                      <Input
                        placeholder="e.g. 1.month or Monthly"
                        value={row.supercycle_billing_interval}
                        onChange={(e) =>
                          setTierRows((p) => p.map((x) => (x.key === row.key ? { ...x, supercycle_billing_interval: e.target.value } : x)))
                        }
                      />
                    </div>
                    <div className="space-y-1">
                      <Label className="text-xs">Minimum term (Supercycle: N.unit)</Label>
                      <Input
                        placeholder="e.g. 2.month or 2 months"
                        value={row.supercycle_minimum_term}
                        onChange={(e) =>
                          setTierRows((p) => p.map((x) => (x.key === row.key ? { ...x, supercycle_minimum_term: e.target.value } : x)))
                        }
                      />
                    </div>
                    <div className="space-y-1">
                      <Label className="text-xs">Billing every (count)</Label>
                      <Input
                        value={row.billing_interval_count}
                        onChange={(e) =>
                          setTierRows((p) => p.map((x) => (x.key === row.key ? { ...x, billing_interval_count: e.target.value } : x)))
                        }
                      />
                    </div>
                    <div className="space-y-1">
                      <Label className="text-xs">Billing unit</Label>
                      <Input
                        value={row.billing_interval_unit}
                        onChange={(e) =>
                          setTierRows((p) => p.map((x) => (x.key === row.key ? { ...x, billing_interval_unit: e.target.value } : x)))
                        }
                      />
                    </div>
                    <div className="space-y-1 sm:col-span-2">
                      <Label className="text-xs">Shopify selling plan id (optional)</Label>
                      <Input
                        value={row.shopify_selling_plan_id}
                        onChange={(e) =>
                          setTierRows((p) => p.map((x) => (x.key === row.key ? { ...x, shopify_selling_plan_id: e.target.value } : x)))
                        }
                      />
                    </div>
                  </div>
                </div>
              ))}
              <div className="flex flex-wrap gap-2">
                <Button type="button" variant="outline" size="sm" className="gap-1" onClick={() => setTierRows((p) => [...p, emptyTierRow()])}>
                  <Plus className="h-4 w-4" />
                  Add tier
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    setTierRows(defaultTierRowsForMonths());
                    toast.message('Replaced with default 1, 2, 3, 6, 12, 24 month rows');
                  }}
                >
                  Use default 1–24 month set
                </Button>
              </div>
            </div>
          )}

          <DialogFooter className="gap-2 sm:gap-0">
            <Button type="button" variant="outline" onClick={() => setEditorOpen(false)} disabled={editorSaving}>
              Cancel
            </Button>
            <Button type="button" onClick={saveTierEditor} disabled={editorLoading || editorSaving}>
              {editorSaving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
              Save tiers
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      </main>
    </div>
  );
}
